import { SYSTableDefIds, html, SYSLongRunningProcess } from "../../"
import { T, _HasPermission } from "../../global/globals"
import { HookTablePrepareFn } from "../../global/hooks"
import { SYSBaseElement } from "../sysbaseelement"
import type { TableDataAndDef } from "./table"
import { TableGet, TableRebuild, TableRowFnExecute } from "./tableapi"

import type {
  FormFnCan_Visibility,
  HookTablePrepareFn_ReturnType,
  HookTablePrepareFn_Type,
} from "../../global/hooks"

import type { SYSDialog } from "../.."
import { DialogClose, DialogIsOpen } from "../dialog/dialogapi"
import {
  ProcessEnd,
  ProcessIsRunning,
  ProcessStart,
} from "../longrunningprocesstracker/processapi"
import {
  _FilterChangedComparedToDefault,
  _SortChangedComparedToDefault,
} from "./tablerowheadermenu/tablesettings/helpers"
import { _HasForcedFilter, _ResetFilters, _ResetSort } from "./helpers"
import { StartScreenUpdate } from "../../tracker/proxyhandler"
import { FeatureInfo } from "../../global/globalapi"

type NavType = "header" | "row"

export type NavDef = {
  defId: SYSTableDefIds
  type: NavType
  position?: "top" | "bottom"
}

export class SYSTableNavControl extends SYSBaseElement {
  props: any
  nav: NavDef
  tableData: TableDataAndDef
  oldDefId: string

  handleFilterDefaultClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    await _ResetFilters(this.tableData.def)
    await TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSortDefaultClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    _ResetSort(this.tableData.def)
    await TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSetPageClick = (e: Event, page: number) => {
    e.stopPropagation()
    e.preventDefault()
    ProcessStart("SYSTableRebuild", {
      snack: { type: "BusyIndicator" },
      instance: this.tableData.def.id,
    })
    StartScreenUpdate(0)
    setTimeout(() => {
      try {
        this.actions.ovl.internal._TableSetPage({
          paging: this.tableData.def.uiState.paging,
          page: page,
        })
      } finally {
        ProcessEnd("SYSTableRebuild", this.tableData.def.id)
      }
    }, 0)
  }

  handleMoreTableOptionsClick = (e: Event) => {
    let def = this.tableData.def
    this.actions.ovl.internal._TableMoreRow({
      def,
      data: this.tableData.data,
      key: def.uiState.firstVisibleColumn,
    })
  }

  handleAddRowClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    // if (this.nav.type === "header") {
    //   this.actions.ovl.internal._TableSelectHeader({
    //     def: this.tableData.def,
    //     data: this.tableData.data,
    //     key: "",
    //   })
    if (DialogIsOpen("SYSTableHeaderMenu", "2")) {
      DialogClose("SYSTableHeaderMenu", { instance: "2" })
    } else {
      DialogClose("SYSTableHeaderMenu", { instance: "1" })
    }
    // }
    let addRes = await this.actions.ovl.table._TableAddRow({
      data: this.tableData.data,
      def: this.tableData.def,
    })
    await this.actions.ovl.table._TableEditRow({
      data: this.tableData.data,
      def: this.tableData.def,
      key: addRes.key,
      initialFocusFieldKey: addRes.fieldKeyToFocus,
    })
  }

  handleRefreshClick = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSourceData() {
    let oldNavType = this.nav?.type
    this.nav = this.props(this.state)
    let defId = this.nav.defId
    if (typeof defId === "object") {
      //@ts-ignore
      defId = defId.defId
      this.nav = { ...this.nav, defId, type: undefined }
    }
    if (oldNavType) {
      this.nav.type = oldNavType
    }
    if (this.oldDefId !== this.nav.defId) {
      this.oldDefId = this.nav.defId
      if (!this.nav.type) {
        this.nav.type = "row"
      }
      let tb = TableGet(this.nav.defId)
      this.tableData = { def: tb.def, data: tb.dataDef }
    }
  }

  async init() {
    this.handleSourceData()
    await super.init()
  }
  async getUI() {
    this.handleSourceData()
    let navbuttonsclass = "ovl-table-navbuttons"
    let tableDef = this.tableData.def
    let feature = tableDef.features
    let customFnHook =
      this.actions.app.features?.[tableDef.feature]?.hooks?.[HookTablePrepareFn]
    let customFnHookResult: HookTablePrepareFn_ReturnType

    const isVisible = (controlKey: string) => {
      let tc = tableDef.options.rendering?.tableControls?.[controlKey]

      // Table Header Menu Bypass
      if (this.nav.type === "header") {
        if (controlKey === "add" && !tableDef.features.add) return false
        if (tc && tc.visible === false) return false
        return true
      }

      // Row Navigation Controls position filtering
      if (!tc || !tc.visible) return false
      if (this.nav.position === "top" && tc.position === "bottom") return false
      if (this.nav.position === "bottom" && tc.position === "top") return false

      return true
    }

    let tcAll = tableDef.options.rendering?.tableControls
    let hasTop =
      tcAll &&
      Object.keys(tcAll).some(
        (k) =>
          tcAll[k].visible &&
          (tcAll[k].position === "top" || tcAll[k].position === "top/bottom")
      )

    const posClass = (controlKey: string) => {
      let tc = tableDef.options.rendering?.tableControls?.[controlKey]
      if (
        this.nav.type === "row" &&
        this.nav.position === "bottom" &&
        tc?.position === "top/bottom"
      ) {
        let isTopNavVisible = true
        let parentTable = this.closest("ovl-table") as any
        if (parentTable) {
          let topNav = parentTable.querySelector(".ovl-nav-top") as HTMLElement
          let scrollParent = (parentTable.closest(".ovl-screen-scroll-container") ||
            parentTable.closest(".ovl-dialog-body") ||
            parentTable.closest(".scrollable") ||
            parentTable.closest(".ovl-body-scroll")) as HTMLElement
          if (topNav && scrollParent) {
            const topNavRect = topNav.getBoundingClientRect()
            const parentRect = scrollParent.getBoundingClientRect()
            isTopNavVisible = topNavRect.bottom > parentRect.top && topNavRect.top < parentRect.bottom
          }
        }
        return isTopNavVisible && hasTop
          ? "ovl-nav-pos-top-bottom ovl-hide-nav"
          : "ovl-nav-pos-top-bottom"
      }
      return ""
    }

    let filterDefaultButton = null
    if (
      (_FilterChangedComparedToDefault(tableDef) ||
        tableDef.uiState.filter.filter.forcedFilterKeys) &&
      isVisible("resetFilter")
    ) {
      if (customFnHook) {
        customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
          data: this.tableData.data,
          def: tableDef,
          values: { ResetFilter: { description: T("SYSFilterReset") } },
          mode: "HeaderMenu",
          type: "Table",
          columnKey: undefined,
        })
      }

      let visibility: FormFnCan_Visibility = "Visible"
      let icon = "sap-icon--clear-filter"
      let disabled = false
      let filterClearTooltip = T("SYSFilterReset")

      if (customFnHookResult?.OvlResetFilter) {
        if (customFnHookResult.OvlResetFilter.visibility) {
          visibility = customFnHookResult.OvlResetFilter.visibility
        }
        if (customFnHookResult.OvlResetFilter.icon) {
          icon = customFnHookResult.OvlResetFilter.icon
        }
        if (customFnHookResult.OvlResetFilter.tooltip) {
          filterClearTooltip = <string>customFnHookResult.OvlResetFilter.tooltip
        }
      }

      if (visibility === "Disabled") {
        disabled = true
      }

      if (visibility !== "NotVisible") {
        let forcedActive = _HasForcedFilter(tableDef, true)
        filterDefaultButton = html`
          <button
            title="${filterClearTooltip}"
            @click="${(e) => this.handleFilterDefaultClick(e)}"
            ?disabled=${disabled}
            class="fd-button ${navbuttonsclass} ${forcedActive
              ? "__filterforced"
              : "__filteractive"} ${posClass("resetFilter")}"
          >
            <i class="${icon}"></i>
          </button>
        `
      }
    }

    let sortDefaultButton = null
    if (
      this.tableData.def.uiState.rowsCount > 1 &&
      _SortChangedComparedToDefault(tableDef) &&
      isVisible("resetSort")
    ) {
      let sortClearTooltip = T("SYSSortReset")
      sortDefaultButton = html`
        <button
          title="${sortClearTooltip}"
          @click="${(e) => this.handleSortDefaultClick(e)}"
          class="fd-button ${navbuttonsclass} ${posClass("resetSort")}"
        >
          <i class="sap-icon--sort"></i>
        </button>
      `
    }

    let refresh = null
    if (
      this.nav.type === "row" &&
      !ProcessIsRunning("SYSTableRebuild") &&
      this.tableData.def.uiState.rebuild.needsRefresh === true &&
      isVisible("rebuild")
    ) {
      refresh = html`
        <button
          @click=${(e) => this.handleRefreshClick(e)}
          class="fd-button ${navbuttonsclass} ${posClass("rebuild")}"
          title="${T("SYSTableRebuild")}"
        >
          <i class="sap-icon--refresh"></i>
        </button>
      `
    }

    let add
    if (
      _HasPermission(tableDef.permission, "A") &&
      feature.add &&
      isVisible("add")
    ) {
      let values = {
        SYSAdd: {
          description: `${T(
            tableDef.title.dialogTitle.entityNameTranslationKey
          )}: ${T("SYSAdd")}`,
        },
      }

      if (customFnHook) {
        customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
          data: this.tableData.data,
          def: tableDef,
          values,
          mode: "HeaderMenu",
          type: "Table",
          columnKey: undefined,
        })
      }

      let visibility: FormFnCan_Visibility = "Visible"
      let icon = "sap-icon--add"
      let disabled = false
      let tooltip = `${T(
        this.tableData.def.title.dialogTitle.entityNameTranslationKey
      )}: ${T("SYSAdd")}`

      if (tableDef.defFromServer && tableDef.defFromServer.CanAdd === false) {
        visibility = "Disabled"
      }

      if (customFnHookResult?.SYSAdd) {
        if (customFnHookResult.SYSAdd.icon) {
          icon = customFnHookResult.SYSAdd.icon
        }
        if (customFnHookResult.SYSAdd.visibility) {
          visibility = customFnHookResult.SYSAdd.visibility
        }
      }
      if (visibility === "Disabled") {
        disabled = true
      }

      if (visibility !== "NotVisible") {
        add = html`
          <button
            @click=${(e) => this.handleAddRowClick(e)}
            class="fd-button ${navbuttonsclass} ${posClass("add")}"
            id="ovl_nav_add${this._id}"
            title="${tooltip}"
            ?disabled=${disabled}
          >
            <i class="${icon}"></i>
          </button>
        `
      }
    }
    let more = null
    if (
      this.nav.type === "row" &&
      (feature.filter || feature.multiSelect) &&
      feature.headerMenu &&
      isVisible("more")
    ) {
      more = html`
        <button
          title="${T("SYSTableMenu")}"
          @click="${(e) => this.handleMoreTableOptionsClick(e)}"
          class="fd-button ${navbuttonsclass} ${posClass("more")}"
          id="ovl_nav_more${this._id}"
        >
          <i class="sap-icon--overflow"></i>
        </button>
      `
    }

    let tablenav

    let paging = this.tableData.def.uiState.paging
    let dataFilteredAndSorted = this.tableData.def.uiState.dataFilteredAndSorted

    if (isVisible("page")) {
      //console.log(this.tableData.def.id)
      let rebuildInProcess = ProcessIsRunning(
        "SYSTableRebuild",
        this.tableData.def.id
      )
      let count = dataFilteredAndSorted.length
      if (count > paging.pageSize) {
        let pages = Math.ceil(count / paging.pageSize)
        tablenav = html`
          <div
            class="fd-segmented-button ${posClass("page")}"
            role="group"
            aria-label="Tablecontrol"
          >
            <button
              title="Erster Datensatz"
              ?disabled=${paging.page === 0 || pages === 1 || rebuildInProcess}
              @click=${(e) => this.handleSetPageClick(e, 0)}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-rewind"></i>
            </button>
            <button
              ?disabled=${paging.page === 0 || rebuildInProcess}
              title="Vorheriger Datensatz"
              @click=${(e) => this.handleSetPageClick(e, paging.page - 1)}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-reverse"></i>
            </button>
            <button
              class=${`fd-button ${navbuttonsclass} ovl-table-navbuttons-info`}
            >
              ${paging.page + 1}/${pages}
            </button>
            <button
              title="Nächster Datensatz"
              @click=${(e) => this.handleSetPageClick(e, paging.page + 1)}
              ?disabled=${paging.page + 1 === pages || rebuildInProcess}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-play"></i>
            </button>
            <button
              title="Letzter Datensatz"
              @click=${(e) => this.handleSetPageClick(e, pages - 1)}
              ?disabled=${paging.page + 1 === pages ||
              pages === 1 ||
              rebuildInProcess}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-forward"></i>
            </button>
          </div>
        `
      }
    }
    let autoCloseButtonIfInDialog
    if (this.nav.type === "row" && this.nav.position !== "top") {
      let dialogHolderEl = <any>this.closest("ovl-dialogholder")
      if (
        dialogHolderEl &&
        dialogHolderEl.dialogHolderParams?.dialogParts
          ?.tableDefButtonsInFooter === tableDef.id
      ) {
        let dialogEl = <SYSDialog>(<any>this.closest(".ovl-dialog"))
        if (dialogEl) {
          //@ts-ignore
          let ids = dialogEl.id.split("-")
          let dialogType = ids[2]
          let dialogInstance = ids[3]
          autoCloseButtonIfInDialog = html` <button
            @click="${(e) =>
              DialogClose(dialogType, { instance: dialogInstance })}"
            title="${T("SYSCloseDialog")}"
            class="fd-button fd-button--positive ovl-margin-corr2 ovl-nav-pos-bottom"
          >
            <i class="sap-icon--decline"></i>
          </button>`
        }
      }
    }
    let customTableFunctions: any = []
    if (
      tableDef.options.customRowActions &&
      this.nav.type === "row" &&
      isVisible("customFns")
    ) {
      let rowActions = tableDef.options.customRowActions
      let actionsInTableNav = Object.keys(rowActions).filter(
        (k) =>
          rowActions[k].mode === "Table" &&
          rowActions[k].showInTableNav &&
          (!rowActions[k].permission ||
            _HasPermission(
              rowActions[k].permission,
              rowActions[k].permissionModifier
            )) &&
          (!rowActions[k].role || FeatureInfo(rowActions[k].role))
      )
      if (actionsInTableNav.length > 0) {
        let values = {}
        actionsInTableNav.forEach((k) => {
          values[k] = {
            description: rowActions[k].translationKey
              ? T(rowActions[k].translationKey)
              : k,
          }
        })

        if (customFnHook) {
          customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
            data: this.tableData.data,
            def: tableDef,
            values,
            mode: "HeaderMenu",
            type: "Table",
            columnKey: undefined,
          })
        }

        let table = TableGet(tableDef.id)
        let selectedKeys = table.getKeys({ byKeys: "Selected" })
        let selectedCount = selectedKeys.length

        customTableFunctions = actionsInTableNav.map((k) => {
          let visibility: FormFnCan_Visibility = "Visible"
          let icon = rowActions[k].icon
          let disabled = false
          let tooltip = values[k].description

          if (rowActions[k].whenMultiple && selectedCount === 0) {
            disabled = true
          }

          if (customFnHookResult?.[k]) {
            if (customFnHookResult[k].icon) {
              icon = customFnHookResult[k].icon
            }
            if (customFnHookResult[k].visibility) {
              visibility = customFnHookResult[k].visibility
            }
            if (customFnHookResult[k].res) {
              tooltip = customFnHookResult[k].res
            }
            if (customFnHookResult[k].tooltip) {
              tooltip = customFnHookResult[k].tooltip
            }
            if (customFnHookResult[k].visibility === "Disabled") {
              disabled = true
            } else if (customFnHookResult[k].visibility === "Visible") {
              disabled = false
            }
          }

          if (visibility !== "NotVisible") {
            let atLeastOneMultiProcessRunning = false
            if (rowActions[k].process && rowActions[k].process !== "None") {
              atLeastOneMultiProcessRunning = ProcessIsRunning(
                <SYSLongRunningProcess>rowActions[k].process
              )
            }

            return html`
              <button
                @click=${async (e: Event) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (!disabled && !atLeastOneMultiProcessRunning) {
                    await TableRowFnExecute(
                      tableDef.id,
                      k,
                      rowActions[k].whenMultiple ? selectedKeys : []
                    )
                  }
                }}
                class="fd-button ${navbuttonsclass} ${posClass("customFns")}"
                id="ovl_nav_fn_${k}${this._id}"
                title="${tooltip}"
                ?disabled=${disabled || atLeastOneMultiProcessRunning}
              >
                <i class="${icon}"></i>
              </button>
            `
          }
          return null
        })
      }
    }
    return html` ${tablenav} ${filterDefaultButton} ${sortDefaultButton} ${add}
    ${customTableFunctions} ${refresh} ${more} ${autoCloseButtonIfInDialog}`
  }
}
